import React, { useState, useEffect } from 'react'
import { api } from '../../utils/api'
import DealLink from '../shared/DealLink.jsx'

/**
 * The payout instrument on file for a customer.
 *
 * Masked until Reveal is pressed, the same way the payments page works, and for
 * the same reason: a bank account should not be sitting on screen behind
 * somebody in an office. Reveal is a POST that writes an audit line naming who
 * looked, so it is a deliberate act rather than a side effect of opening a page.
 *
 * Saving here reaches every OPEN deal — not only those in the payment queue — and the card says how
 * many it reached. An operator correcting an account needs to know the payment
 * run picked it up, not just that something somewhere was saved.
 */
function CustomerPayoutCard({ customerId }) {
  const [payout, setPayout] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [revealed, setRevealed] = useState(null)
  const [revealing, setRevealing] = useState(false)

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState({ method: 'bank' })
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      // Anything revealed belongs to the customer we were looking at, not the
      // one we are about to look at.
      setRevealed(null)
      try {
        const result = await api.get(`/api/customers/${encodeURIComponent(customerId)}/payout`)
        if (!cancelled) setPayout(result.payout)
      } catch (e) {
        if (!cancelled) {
          // 404 is "none on file", which is a normal state and not an error.
          if (e.status === 404) setPayout(null)
          else setError(e.message || 'Could not load payout details')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [customerId])

  async function reveal() {
    setRevealing(true)
    setMessage(null)
    try {
      const result = await api.post(
        `/api/customers/${encodeURIComponent(customerId)}/payout/reveal`,
        {}
      )
      setRevealed(result.payout)
    } catch (e) {
      setMessage(e.message || 'Could not reveal these details')
    } finally {
      setRevealing(false)
    }
  }

  const EMPTY_DRAFT = { method: 'bank', bank_name: '', bank_account: '', bank_sort: '', paypal_email: '' }

  /**
   * Open the editor with the CURRENT details in it.
   *
   * The card only holds masked values, so this reveals first. Editing bank
   * details without seeing them is not really editing: correcting one wrong
   * digit would mean retyping the whole account from somewhere else, and a
   * blank form next to "Save" is an invitation to overwrite details that were
   * fine.
   *
   * The reveal is audited exactly like pressing Reveal, because it is the same
   * act — the operator is looking at the account.
   */
  async function startEditing() {
    setMessage(null)

    if (!payout) {
      setDraft(EMPTY_DRAFT)
      setEditing(true)
      return
    }

    setRevealing(true)
    try {
      const result = await api.post(
        `/api/customers/${encodeURIComponent(customerId)}/payout/reveal`,
        {}
      )
      const current = result.payout || {}
      setDraft({
        method: current.method || payout.method || 'bank',
        bank_name: current.bank_name || '',
        bank_account: current.bank_account || '',
        bank_sort: current.bank_sort || '',
        paypal_email: current.paypal_email || '',
      })
      setEditing(true)
    } catch (e) {
      // Editable anyway, but say so plainly: a Save from a blank form REPLACES
      // the instrument, so somebody who does not know the fields failed to load
      // could wipe details that were perfectly good.
      setDraft({ ...EMPTY_DRAFT, method: payout.method || 'bank', bank_name: payout.bank_name || '' })
      setEditing(true)
      setMessage(
        'Could not load the current details to edit. Anything you save will replace them completely.'
      )
    } finally {
      setRevealing(false)
    }
  }

  async function save(event) {
    event.preventDefault()
    setSaving(true)
    setMessage(null)
    try {
      const result = await api.put(
        `/api/customers/${encodeURIComponent(customerId)}/payout`,
        {
          method: draft.method,
          bank_name: draft.method === 'bank' ? (draft.bank_name || null) : null,
          bank_account: draft.method === 'bank' ? (draft.bank_account || null) : null,
          bank_sort: draft.method === 'bank' ? (draft.bank_sort || null) : null,
          paypal_email: draft.method === 'paypal' ? (draft.paypal_email || null) : null,
        }
      )
      setPayout(result.payout)
      // Whatever was on screen is now the old instrument.
      setRevealed(null)
      setEditing(false)
      // "Open deals", NOT "waiting to be paid". Prism pushes the instrument
      // onto every non-terminal quote deliberately, so a customer who changes
      // bank while their gear is still on the bench does not have to change it
      // again once the quote reaches the payment queue. The old wording said
      // "waiting to be paid" and reported 2 for a customer with one deal in
      // that queue, which reads as a bug in the write rather than a caption
      // describing a wider set than the reader expected.
      setMessage(
        result.quotes_updated === 1
          ? 'Saved. 1 open deal was updated.'
          : `Saved. ${result.quotes_updated} open deals were updated.`
      )
    } catch (e) {
      setMessage(e.message || 'Could not save payout details')
    } finally {
      setSaving(false)
    }
  }

  async function remove() {
    // Confirmed because it is not recoverable from this screen, and because
    // "delete" next to bank details deserves a moment's pause.
    if (!window.confirm('Remove these payout details from the customer? Deals already waiting to be paid keep theirs.')) return
    setSaving(true)
    setMessage(null)
    try {
      await api.delete(`/api/customers/${encodeURIComponent(customerId)}/payout`)
      setPayout(null)
      setRevealed(null)
      setMessage('Removed. Deals waiting to be paid keep the details they had.')
    } catch (e) {
      setMessage(e.message || 'Could not remove payout details')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <h2 style={{ marginTop: 0 }}>Payment details</h2>
        {!editing && !loading && (
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={revealing}
              onClick={startEditing}
            >
              {revealing ? 'Opening…' : (payout ? 'Edit' : 'Add')}
            </button>
            {payout && (
              <button type="button" className="btn btn-secondary" disabled={saving} onClick={remove}>
                Delete
              </button>
            )}
          </div>
        )}
      </div>

      {loading && <p>Loading payment details…</p>}

      {error && !loading && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}

      {!loading && !error && !payout && !editing && (
        <p>No payment details on file. Add them and any deal waiting to be paid will pick them up.</p>
      )}

      {!loading && !error && payout && !editing && (
        <>
          <table className="table">
            <tbody>
              <tr>
                <th style={{ width: '30%' }}>Method</th>
                <td>{payout.method === 'paypal' ? 'PayPal' : 'Bank transfer'}</td>
              </tr>
              {payout.method === 'bank' && (
                <>
                  <tr><th>Bank</th><td>{payout.bank_name || '-'}</td></tr>
                  <tr>
                    <th>Account</th>
                    <td>{revealed ? revealed.bank_account : (payout.account_last4 ? `••••${payout.account_last4}` : '-')}</td>
                  </tr>
                  <tr>
                    <th>Sort code</th>
                    <td>{revealed ? (revealed.bank_sort || '-') : '••-••-••'}</td>
                  </tr>
                </>
              )}
              {payout.method === 'paypal' && (
                <tr>
                  <th>PayPal</th>
                  <td>{revealed ? revealed.paypal_email : (payout.paypal_email_masked || '-')}</td>
                </tr>
              )}
              <tr>
                <th>Source</th>
                <td>
                  {payout.source === 'hubspot_contact_capture' && 'Recovered from HubSpot, never confirmed since'}
                  {payout.source === 'operator' && 'Entered by an operator'}
                  {payout.source === 'quote' && (
                    <>From deal <DealLink dealId={payout.from_quote_reference} reference={payout.from_quote_reference} />. Save to make it theirs.</>
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Reveal works wherever the details live. Which store they are in
              changes the key, not who may look — and a padlock an operator
              cannot open, on details this card has just shown them we hold,
              protects nothing and only sends them to another page. */}
          {!revealed && (
            <button type="button" className="btn btn-secondary" disabled={revealing} onClick={reveal}>
              {revealing ? 'Revealing…' : 'Reveal'}
            </button>
          )}

          {revealed && (
            <button type="button" className="btn btn-secondary" onClick={() => setRevealed(null)}>
              Hide
            </button>
          )}
        </>
      )}

      {editing && (
        <form onSubmit={save}>
          <table className="table">
            <tbody>
              <tr>
                <th style={{ width: '30%' }}><label htmlFor="payout-method">Method</label></th>
                <td>
                  <select
                    id="payout-method"
                    className="form-input"
                    value={draft.method}
                    disabled={saving}
                    onChange={e => setDraft(prev => ({ ...prev, method: e.target.value }))}
                  >
                    <option value="bank">Bank transfer</option>
                    <option value="paypal">PayPal</option>
                  </select>
                </td>
              </tr>

              {draft.method === 'bank' && (
                <>
                  <tr>
                    <th><label htmlFor="payout-bank-name">Bank</label></th>
                    <td>
                      <input
                        id="payout-bank-name" className="form-input" type="text"
                        value={draft.bank_name || ''} disabled={saving}
                        onChange={e => setDraft(prev => ({ ...prev, bank_name: e.target.value }))}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th><label htmlFor="payout-account">Account number</label></th>
                    <td>
                      <input
                        id="payout-account" className="form-input" type="text" inputMode="numeric"
                        value={draft.bank_account || ''} disabled={saving}
                        onChange={e => setDraft(prev => ({ ...prev, bank_account: e.target.value }))}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th><label htmlFor="payout-sort">Sort code</label></th>
                    <td>
                      <input
                        id="payout-sort" className="form-input" type="text"
                        value={draft.bank_sort || ''} disabled={saving}
                        onChange={e => setDraft(prev => ({ ...prev, bank_sort: e.target.value }))}
                      />
                    </td>
                  </tr>
                </>
              )}

              {draft.method === 'paypal' && (
                <tr>
                  <th><label htmlFor="payout-paypal">PayPal email</label></th>
                  <td>
                    <input
                      id="payout-paypal" className="form-input" type="email"
                      value={draft.paypal_email || ''} disabled={saving}
                      onChange={e => setDraft(prev => ({ ...prev, paypal_email: e.target.value }))}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Said plainly before they save, not discovered afterwards. */}
          <p style={{ color: 'var(--color-text-muted)' }}>
            Saving updates every open deal, not only the ones already in the
            payment queue — so a customer who changes bank while their gear is
            still with us does not have to change it again later. Deals already
            closed keep the account their money went to.
            {draft.method === 'paypal' && payout?.method === 'bank' && (
              <> Switching to PayPal replaces their bank details entirely.</>
            )}
            {draft.method === 'bank' && payout?.method === 'paypal' && (
              <> Switching to a bank transfer replaces their PayPal address entirely.</>
            )}
          </p>

          {message && <p style={{ color: 'var(--color-danger)' }}>{message}</p>}

          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button" className="btn btn-secondary" disabled={saving}
              onClick={() => { setEditing(false); setMessage(null) }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {!editing && message && <p><strong>{message}</strong></p>}
    </div>
  )
}

export default CustomerPayoutCard
